import React, { useState } from 'react';
import Input from '../Input';
import Button from '../Button';

interface VotingSession {
  sessionId: string;
  sessionNumber: number;
  type: 'open' | 'anonymous';
  question: string;
  status: 'scheduled' | 'active' | 'completed' | 'canceled';
  startDate: string;
  endDate: string;
  connectedGoals?: string[];
  connectedMilestones?: string[];
  connectedDocuments?: string[];
}

interface InlineEditVotingSessionProps {
  session: VotingSession;
  onSave: (sessionId: string, updates: Partial<VotingSession>) => Promise<void> | void;
  disabled?: boolean;
  className?: string;
  showEditButton?: boolean;
}

const InlineEditVotingSession: React.FC<InlineEditVotingSessionProps> = ({
  session,
  onSave,
  disabled = false,
  className = '',
  showEditButton = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    question: session.question,
    startDate: session.startDate ? new Date(session.startDate).toISOString().slice(0, 16) : '',
    endDate: session.endDate ? new Date(session.endDate).toISOString().slice(0, 16) : '',
    type: session.type,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Only allow editing scheduled sessions
  const canEdit = session.status === 'scheduled' && !disabled;

  const handleStartEdit = () => {
    if (!canEdit) return;
    setIsEditing(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates: Partial<VotingSession> = {
        question: formData.question,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : session.startDate,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : session.endDate,
        type: formData.type,
      };
      await onSave(session.sessionId, updates);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving voting session:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      question: session.question,
      startDate: session.startDate ? new Date(session.startDate).toISOString().slice(0, 16) : '',
      endDate: session.endDate ? new Date(session.endDate).toISOString().slice(0, 16) : '',
      type: session.type,
    });
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg p-4 space-y-4 animate-slide-down ${className}`}>
        <div>
          <Input
            label="Question"
            value={formData.question}
            onChange={(e) => setFormData((prev) => ({ ...prev, question: e.target.value }))}
            placeholder="What should citizens vote on?"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Start Date & Time"
            type="datetime-local"
            value={formData.startDate}
            onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
            required
          />
          <Input
            label="End Date & Time"
            type="datetime-local"
            value={formData.endDate}
            onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Voting Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData((prev) => ({ ...prev, type: e.target.value as 'open' | 'anonymous' }))}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            <option value="open">Open (Registered Users)</option>
            <option value="anonymous">Anonymous</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            isLoading={isSaving}
            disabled={isSaving}
          >
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`inline-edit-display group ${className}`}>
      <div className="inline-edit-content">
        <div className="space-y-2">
          <p className="font-medium text-gray-900">{session.question}</p>
          <div className="text-sm text-gray-600">
            <p>Start: {new Date(session.startDate).toLocaleString()}</p>
            <p>End: {new Date(session.endDate).toLocaleString()}</p>
            <p className="capitalize">Type: {session.type}</p>
          </div>
        </div>
      </div>
      {canEdit && showEditButton && (
        <button
          type="button"
          onClick={handleStartEdit}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 ml-2"
          aria-label="Edit voting session"
        >
          <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default InlineEditVotingSession;

